import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Icon, Input } from '@aqarmap/aqarmap-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useCardValidation from '~/components/UserCardForm/useCardValidation';
import MaskedInputController from '~/components/MaskedInputController';
import {
  CARD_NUMBER_MASK,
  CVV_NUMBER_MASk,
  EXPIRY_DATE_MASK,
} from '~/constants/masks';
import {
  UserCardFormHandle,
  UserCardFormProps,
  PayfortFormHandle,
} from '~/components/UserCardForm/types';
import PayfortForm from '~/components/UserCardForm/PayfortForm';
import { NewCardPaymentPayPayload } from '~/types/paymentTypes';

const UserCardForm = forwardRef<UserCardFormHandle, UserCardFormProps>(
  ({ onFailure }, ref) => {
    const payfortFormRef = useRef<PayfortFormHandle>(null);
    const userCardFormRef = useRef<HTMLFormElement | null>(null);
    const { t } = useTranslation('app');
    const { userCardSchema } = useCardValidation();
    type UserCardFormType = z.infer<typeof userCardSchema>;
    const { control, handleSubmit } = useForm<UserCardFormType>({
      resolver: zodResolver(userCardSchema),
      mode: 'onSubmit',
    });

    const handleFormSubmit = async (
      formData: UserCardFormType,
      tokenizationParams: NewCardPaymentPayPayload,
    ) => {
      const [month, year] = formData.masked_expiry_date.split('/');
      const formattedExpiryDate =
        year.padStart(2, '0') + month.padStart(2, '0');
      payfortFormRef.current?.submitPayfort({
        inputs: {
          card_number: formData.masked_card_number.replace(/ /g, ''),
          card_holder_name: formData.card_holder_name,
          expiry_date: formattedExpiryDate,
          card_security_code: formData?.card_security_code,
        },
        params: tokenizationParams,
      });
    };

    useImperativeHandle(ref, () => ({
      submitUserCard: (tokenizationParams) => {
        handleSubmit((formData) =>
          handleFormSubmit(formData, tokenizationParams),
        )();
      },
    }));

    return (
      <>
        <PayfortForm ref={payfortFormRef} onFailure={onFailure} />
        <form
          className="add-card-form"
          ref={userCardFormRef}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex flex-col gap-3x ">
            <MaskedInputController
              control={control}
              mask={CARD_NUMBER_MASK}
              className="card-number-input"
              label={{
                className: 'text-title_4 text-black',
                text: t('cardNumber'),
              }}
              field={{
                name: 'masked_card_number',
                placeholder: t('cardNumberPlaceholder'),
              }}
              startIcon={<Icon name="visa" className="text-gray__dark_2" />}
            />

            <Controller
              control={control}
              name="card_holder_name"
              render={({ field, fieldState: { error } }) => (
                <Input
                  label={{
                    text: t('holderName'),
                    className: 'text-body_1 text-black',
                  }}
                  field={{
                    placeholder: t('holderNamePlaceholder'),
                    ...field,
                  }}
                  error={error?.message}
                />
              )}
            />
            <div className="flex gap-3x ">
              <MaskedInputController
                control={control}
                mask={EXPIRY_DATE_MASK}
                label={{
                  text: t('expiryDate'),
                  className: 'text-body_1 text-black',
                }}
                field={{
                  name: 'masked_expiry_date',
                  placeholder: 'mm/yy',
                }}
              />
              <MaskedInputController
                control={control}
                mask={CVV_NUMBER_MASk}
                label={{
                  text: t('cvv'),
                  className: 'text-body_1 text-black',
                }}
                field={{
                  name: 'card_security_code',
                  placeholder: t('cvvPlaceholder'),
                }}
              />
            </div>
          </div>
        </form>
      </>
    );
  },
);

export default UserCardForm;
